import { parseISO } from "date-fns";
import { z } from "zod";

import { GoalTransparency } from "@performyard/models";

import request from "../base-request";

const redactedGoalSchema = z.object({
	_id: z.string(),
	transparency: z.literal("private"),
});

export type RedactedGoal = z.infer<typeof redactedGoalSchema>;

const goalSchema = z.object({
	assignee: z.object({
		_id: z.string(),
		name: z.string(),
	}),
	categories: z
		.array(
			z.object({
				_id: z.string(),
				name: z.string(),
			})
		)
		.optional(),
	checklist: z
		.array(
			z.object({
				_id: z.string(),
				text: z.string(),
				checked: z.boolean(),
			})
		)
		.optional(),
	color: z
		.object({
			_id: z.string(),
			name: z.string(),
			color: z.string().optional(),
		})
		.optional()
		.nullable(),
	supported_by: z
		.array(
			z.object({
				_id: z.string(),
				name: z.string(),
				value: z.number().nullable(),
				target: z.number(),
				transparency: GoalTransparency,
			})
		)
		.optional(),
	private_supported_by: z.array(redactedGoalSchema).optional(),
	alignment: z
		.object({
			_id: z.string().optional(),
			name: z.string().optional(),
			value: z.number().nullable().optional(),
			target: z.number().optional(),
			status: z.string().optional(),
			color: z
				.object({
					_id: z.string(),
					name: z.string(),
					color: z.string().optional(),
				})
				.optional()
				.nullable(),
			transparency: GoalTransparency,
		})
		.optional()
		.nullable(),
	private_alignment: redactedGoalSchema.nullable().optional(),
	top_goal: z
		.object({
			_id: z.string(),
			name: z.string(),
			value: z.number().nullable(),
			target: z.number(),
		})
		.optional(),
	created_date: z
		.string()
		.transform((val) => parseISO(val))
		.pipe(z.date())
		.optional(),
	description: z.string(),
	end_date: z
		.string()
		.optional()
		.transform((val) => (val ? parseISO(val) : null))
		.pipe(z.date())
		.nullable(),
	last_update: z
		.string()
		.optional()
		.transform((val) => (val ? parseISO(val) : null))
		.pipe(z.date())
		.nullable(),
	goal_weight: z.number().optional().nullable(),
	is_top: z.boolean().optional(),
	name: z.string(),
	sharers: z
		.array(
			z.object({
				_id: z.string(),
				name: z.string(),
			})
		)
		.optional(),
	start_date: z
		.string()
		.transform((val) => parseISO(val))
		.pipe(z.date()),
	status: z.string().nullable(),
	target: z.number(),
	transparency: GoalTransparency,
	units: z.string(),
	value: z.number().catch(0),
	_id: z.string(),
	checklist_updates_goal_progress: z.boolean().optional(),
});

const flatGoalSchema = z.object({
	assignee: z.string(),
	categories: z.array(z.string()).optional(),
	checklist: z.array(z.string()).optional(),
	color: z.string().optional().nullable(),
	supported_by: z.array(z.string()).optional(),
	alignment: z.string().optional().nullable(),
	sharers: z.array(z.string()).optional(),
});

const originalStrategySchema = z.object(
	Object.keys(goalSchema.shape).reduce(
		(acc, key) => {
			const value = goalSchema.shape[key];
			const flatValueExists = flatGoalSchema.shape[key] !== undefined; // Check if the key exists in flatGoalSchema
			if (flatValueExists) {
				acc[key] = value.or(flatGoalSchema.shape[key]); // If it exists, use the original value from goalSchema
			} else {
				acc[key] = value;
			}
			return acc;
		},
		{} as Record<string, z.ZodType<typeof goalSchema>>
	)
);

export type Goal = z.infer<typeof goalSchema>;

export const goalResponseSchema = z.object({
	errors: z.array(z.string()),
	goal: goalSchema,
	warnings: z.array(z.string()),
});

export type IGoalResponse = {
	errors: string[];
	goal: Goal;
	warnings: string[];
};

export const fetchGoalDetails = async (goalID: string) => {
	const [goalData] = await request<IGoalResponse>(`/api/goal-details/${goalID}`, {
		method: "GET",
	});

	return goalResponseSchema.parse(goalData);
};

export const GoalHistoryActions = [
	"update",
	"add-sharer",
	"create",
	"remove-sharer",
	"add-category",
	"remove-category",
	"add-checklist",
	"delete-checklist",
	"check-checklist",
	"uncheck-checklist",
] as const;

const goalHistorySchema = z.object({
	action: z.enum(GoalHistoryActions).optional().catch("update"),
	actor: z
		.object({
			_id: z.string(),
			name: z.string(),
		})
		.nullish(),
	sharer: z
		.object({
			_id: z.string(),
			name: z.string(),
		})
		.optional(),
	as_of: z
		.string()
		.transform((val) => parseISO(val))
		.pipe(z.date()),
	created_date: z
		.string()
		.transform((val) => parseISO(val))
		.pipe(z.date())
		.optional(),
	created_at: z
		.string()
		.transform((val) => parseISO(val))
		.pipe(z.date())
		.optional(),
	change: z.any().optional(),
	company: z.string().optional(),
	message: z.string().nullable().optional(),
	strategy: z.string().optional(),
	original_strategy: originalStrategySchema.partial().nullish(),
	original_checklist_item: z
		.object({
			_id: z.string().optional(),
			text: z.string().optional(),
			checked: z.boolean().optional(),
		})
		.optional(),
	checklist_item: z
		.object({
			_id: z.string().optional(),
			text: z.string(),
			checked: z.boolean(),
		})
		.optional(),
	_id: z.string(),
	actor_user: z
		.array(
			z.object({
				_id: z.string(),
				name: z.string(),
			})
		)
		.optional(),
	actor_api_token: z
		.array(
			z.object({
				_id: z.string(),
				name: z.string(),
			})
		)
		.optional(),
});

export type GoalHistory = z.infer<typeof goalHistorySchema>;

export const goalHistoryResponseSchema = z.object({
	errors: z.array(z.string()),
	goal_history: z.array(goalHistorySchema).optional(),
	warnings: z.array(z.string()),
});

export type IGoalHistoryResponse = {
	errors: string[];
	goal_history: GoalHistory[];
	warnings: string[];
};

export const fetchGoalDetailHistory = async (
	goalID: string,
	skip = 0,
	take = 100,
	type?: string,
	sortDirection?: "asc" | "desc",
	sortName?: string
) => {
	let url = `/api/goal-history/${goalID}?skip=${skip}&take=${take}`;

	if (type) {
		url = `${url}&type_filter=${type}`;
	}

	if (sortDirection && sortName) {
		url = `${url}&sort=${sortDirection}(${sortName})`;
	}

	try {
		const [goalData] = await request(url);
		const response = goalHistoryResponseSchema.parse(goalData);

		if (response.errors.length) throw new Error(response.errors.join(", "));

		return response.goal_history;
	} catch (e) {
		throw e;
	}
};

export type PatchGoalData = {
	field: string;
	value: any;
}[];

export type GoalChecklistItem = { id?: string; label?: string; checked?: boolean };

// these are the arguments for creating a goal
export type PostGoalData = {
	name: string;
	target: number;
	units: string;
	description: string;
	endDate: "" | Date;
	startDate: "" | Date;
	categories: string[];
	transparency: string;
	alignment: string;
	isTopGoal: boolean;
	checklistUpdatesGoalProgress?: boolean;
	goalWeight: number;
	checklist: GoalChecklistItem[];
	source?: string;
};

export const patchGoal = async (goalID: string, data: PatchGoalData) => {
	const [goalData] = await request<any>(`/api/goal-details/${goalID}`, {
		method: "PATCH",
		body: JSON.stringify({ updates: data }),
	});

	return goalData;
};

export const postGoalChecklistItems = async (goalID: string, newItems: GoalChecklistItem[]) => {
	const [goalData] = await request<any>(`/api/goal-checklist/${goalID}`, {
		method: "POST",
		body: JSON.stringify({ checklist: newItems }),
	});

	return goalData;
};

export const patchGoalChecklistItems = async (goalID: string, newItems: GoalChecklistItem[]) => {
	const [goalData] = await request<any>(`/api/goal-checklist/${goalID}`, {
		method: "PATCH",
		body: JSON.stringify({ checklist: newItems }),
	});

	return goalData;
};

export const deleteGoalChecklistItems = async (goalID: string, checklistIDs: string[]) => {
	const [goalData] = await request<any>(`/api/goal-checklist/${goalID}`, {
		method: "DELETE",
		body: JSON.stringify({ checklist_ids: checklistIDs }),
	});

	return goalData;
};

export const postGoal = async (data: PostGoalData) => {
	const [goalData] = await request<any>("/api/goal-details", {
		method: "POST",
		body: JSON.stringify({ ...data }),
	});

	return goalData;
};

export const deleteGoal = async (goalID: string) => {
	const [goalData] = await request<any>(`/api/goal-details/${goalID}`, {
		method: "DELETE",
	});

	return goalData;
};

export type PostGoalHistoryData = {
	message?: string;
	as_of?: string;
	goal_history_id?: string;
};

export const postGoalHistoryEntry = async (goalID: string, data: PostGoalHistoryData) => {
	const [goalHistoryData] = await request<any>(`/api/goal-history/${goalID}`, {
		method: "POST",
		body: JSON.stringify({ ...data }),
	});

	return goalHistoryData;
};

export const patchGoalHistoryEntry = async (goalHistoryID: string, data: PostGoalHistoryData) => {
	const [goalHistoryData] = await request<any>(`/api/goal-history/${goalHistoryID}`, {
		method: "PATCH",
		body: JSON.stringify({ ...data }),
	});

	return goalHistoryData;
};

export const deleteGoalHistoryEntry = async (goalHistoryID: string, goalID: string) => {
	const [goalHistoryData] = await request<any>(`/api/goal-history/${goalID}`, {
		method: "DELETE",
		body: JSON.stringify({ goal_history_id: goalHistoryID }),
	});

	return goalHistoryData;
};
